import { Box, Tab, Tabs } from '@mui/material';
import { useStore } from 'mobx/StoreContext';
import { observer } from 'mobx-react-lite';
import {
  JobAssignStatus,
  JobQueueTableType,
} from 'pages/iPriseV2/common/enum/TeamUserRoles';
import JobQueueTableCommon from 'pages/iPriseV2/common/JobQueueTable';
import React, { useCallback } from 'react';

import ColumnsSidebar from './ColumnsSidebar';

const JobQueueTable: React.FC = () => {
  const { managerJobQueueService } = useStore();
  const {
    jobSubTab,
    unAllocatedSubTab,
    assignedSubTab,
    unAllocatedPage,
    unAllocatedRowsPerPage,
    assignedPage,
    assignedRowsPerPage,
    readyForReviewPage,
    readyForReviewRowsPerPage,
    readyForApprovedPage,
    readyForApprovedRowsPerPage,
    submittedPage,
    submittedRowsPerPage,
    selectedTeam,
    selectedSort,
    searchTerm,
    percentage,
  } = managerJobQueueService;

  const handleJobSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      managerJobQueueService.setJobSubTab(newValue);
    },
    [managerJobQueueService],
  );

  const handleUnAllocatedSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      managerJobQueueService.setUnAllocatedSubTab(newValue);
    },
    [managerJobQueueService],
  );

  const handleAssignedSubTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      managerJobQueueService.setAssignedSubTab(newValue);
    },
    [managerJobQueueService],
  );

  return (
    <Box className="table-container-with-sidebar">
      <Box className="table-main-content">
        <Box className="sub-tabs-container">
          <Box className="sub-tabs-wrapper">
            <Tabs
              value={jobSubTab}
              onChange={handleJobSubTabChange}
              className="sub-tabs"
              variant="scrollable"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                className: 'sub-tab-indicator',
              }}>
              <Tab
                label={
                  <Box className="tab-label-with-badge">
                    <span className="tab-dot" />
                    Unallocated
                  </Box>
                }
                className="sub-tab"
              />
              <Tab label="Assigned" className="sub-tab" />
              <Tab label="Ready for Review" className="sub-tab" />
              <Tab label="Approved" className="sub-tab" />
              <Tab label="Submitted" className="sub-tab" />
            </Tabs>
          </Box>
        </Box>

        {jobSubTab === 0 && (
          <Box className="sub-tabs-container">
            <Box className="sub-tabs-wrapper">
              <Tabs
                value={unAllocatedSubTab}
                onChange={handleUnAllocatedSubTabChange}
                className="sub-tabs"
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  className: 'sub-tab-indicator',
                }}>
                <Tab label="TL Unallocated" className="sub-tab" />
                <Tab label="Recruiter Unallocated" className="sub-tab" />
              </Tabs>
            </Box>
          </Box>
        )}

        {jobSubTab === 0 && (
          <JobQueueTableCommon
            tableType={JobQueueTableType.MANAGER_UNALLOCATED}
            status={
              unAllocatedSubTab === 0
                ? JobAssignStatus.TL_UNALLOCATED
                : JobAssignStatus.RECRUITER_UNALLOCATED
            }
            queryKey="mgr-job-postings-unallocated"
            page={unAllocatedPage}
            rowsPerPage={unAllocatedRowsPerPage}
            onPageChange={(p) => managerJobQueueService.setUnAllocatedPage(p)}
            onRowsPerPageChange={(r) =>
              managerJobQueueService.setUnAllocatedRowsPerPage(r)
            }
            popperId="skills-popper-mgr-unallocated"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 1 && (
          <Box className="sub-tabs-container">
            <Box className="sub-tabs-wrapper">
              <Tabs
                value={assignedSubTab}
                onChange={handleAssignedSubTabChange}
                className="sub-tabs"
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{
                  className: 'sub-tab-indicator',
                }}>
                <Tab label="TL Assigned" className="sub-tab" />
                <Tab label="Recruiter Assigned" className="sub-tab" />
              </Tabs>
            </Box>
          </Box>
        )}

        {jobSubTab === 1 && (
          <JobQueueTableCommon
            tableType={JobQueueTableType.MANAGER_ASSIGNED}
            status={
              assignedSubTab === 0
                ? JobAssignStatus.TL_ASSIGNED
                : `${JobAssignStatus.RECRUITER_ASSIGNED},${JobAssignStatus.RECRUITER_ACCEPTED}`
            }
            queryKey="mgr-job-postings-assigned"
            page={assignedPage}
            rowsPerPage={assignedRowsPerPage}
            onPageChange={(p) => managerJobQueueService.setAssignedPage(p)}
            onRowsPerPageChange={(r) =>
              managerJobQueueService.setAssignedRowsPerPage(r)
            }
            popperId="skills-popper-mgr-assigned"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 2 && (
          <JobQueueTableCommon
            tableType={JobQueueTableType.MANAGER_READY_FOR_REVIEW}
            status={JobAssignStatus.READY_FOR_REVIEW}
            queryKey="mgr-job-postings-ready-for-review"
            page={readyForReviewPage}
            rowsPerPage={readyForReviewRowsPerPage}
            onPageChange={(p) =>
              managerJobQueueService.setReadyForReviewPage(p)
            }
            onRowsPerPageChange={(r) =>
              managerJobQueueService.setReadyForReviewRowsPerPage(r)
            }
            popperId="skills-popper-mgr-ready-for-review"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 3 && (
          <JobQueueTableCommon
            tableType={JobQueueTableType.MANAGER_APPROVED}
            status={JobAssignStatus.TL_APPROVED}
            queryKey="mgr-job-postings-approved"
            page={readyForApprovedPage}
            rowsPerPage={readyForApprovedRowsPerPage}
            onPageChange={(p) =>
              managerJobQueueService.setReadyForApprovedPage(p)
            }
            onRowsPerPageChange={(r) =>
              managerJobQueueService.setReadyForApprovedRowsPerPage(r)
            }
            popperId="skills-popper-mgr-approved"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
        {jobSubTab === 4 && (
          <JobQueueTableCommon
            tableType={JobQueueTableType.MANAGER_SUBMITTED}
            status={JobAssignStatus.SUBMITTED}
            queryKey="mgr-job-postings-submitted"
            page={submittedPage}
            rowsPerPage={submittedRowsPerPage}
            onPageChange={(p) => managerJobQueueService.setSubmittedPage(p)}
            onRowsPerPageChange={(r) =>
              managerJobQueueService.setSubmittedRowsPerPage(r)
            }
            popperId="skills-popper-mgr-submitted"
            selectedTeam={selectedTeam}
            selectedSort={selectedSort}
            searchTerm={searchTerm}
            percentage={percentage}
          />
        )}
      </Box>
      <ColumnsSidebar />
    </Box>
  );
};

export default observer(JobQueueTable);
